import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from "react-router-dom";
import {
  setBotFilter,
  setDateFilter,
  setGenderFilter,
  setIosUserFilter,
  setLocationStatsTypeFilter,
  setResetFilter,
  setUTMFilter,
  setUtmOptions,
} from '../../containers/app-container/reducer/filterSlice.js';
import CountrySelect from '../country-select/CountrySelect';
import FilterState from '../country-select/FilterState';
import BasicRangeShortcuts from '../date-range-picker/BasicRangeShortcuts';
import { useNotification } from '../../hooks/useNotification.js';
import { fetchUtmList } from '../../api/utm.js'
import { FormControl, Select, MenuItem, Button } from '@mui/material';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import ProfileCompilationDropdown from '../ProfileCompilationDropdown.js';
import { STATUS_LABEL } from '../../utils/memberStatus';

// Location Insights status options -> the server statsType that selects that status.
// `hidden_by_admin` is the canonical Hidden bucket on the server despite its old name.
const LOCATION_STATUS_OPTIONS = [
  { value: 'active_users', status: 'active' },
  { value: 'approved_users', status: 'approved' },
  { value: 'hidden_by_admin', status: 'hidden' },
  { value: 'inactive_users', status: 'inactive' },
  { value: 'ghost_users', status: 'ghost' },
];

const SearchFilter = ({ type, isHideUserManagement = true, isHideSelectCam = true }) => {
  const location = useLocation();
  // Detect page context for locked dropdowns
  const isBotManagement = location.pathname.includes("bot-management");
  // Channel Attribution's member list renders the SAME UserList as User Management and wants
  // the same controls, so it counts as User Management here. This also makes the isBot
  // normaliser below fire on that route — attribution counts are non-bot (CANON_BASE), so
  // without it a stale `isBot: true` from Bot Management would ride in and empty the list.
  const isAttributionMembers = location.pathname.includes("attribution-members");
  const isUserManagement = location.pathname.includes("user-management") || isAttributionMembers;
  const isLocationInsights = location.pathname.includes("location-insights");

  const selectedGender = useSelector((state) => state.filter.gender);
  const selectedLocationStatsType = useSelector((state) => state.filter.locationStatsType);
  const selectedBotFilter = useSelector((state) => state.filter.isBot);
  const selectedIosUserFilter = useSelector((state) => state.filter.isIosUser);
  const selectedCountry = useSelector(state => state.filter.country)
  const utmOptions = useSelector(state => state.filter.utm_options);
  const utmPayload = useSelector(state => state.filter.utm_payload);
  const defaultDateRange = useSelector((state) => state.filter.defaultDateRange);

  const dispatch = useDispatch();
  const { showErrorNotification } = useNotification();

  useEffect(() => {
    if (isBotManagement) {
      dispatch(setBotFilter({ isBot: true }));
    } else if (isUserManagement) {
      dispatch(setBotFilter({ isBot: false }));
    }
  }, [isBotManagement, isUserManagement]);

  useEffect(() => {
    fetchUtmList({
      setUtmOptions: (data) => dispatch(setUtmOptions(data))
    }).catch(() => showErrorNotification("Unable to load UTM options"));
  }, []);

  const handleGenderClick = (value) => {
    dispatch(setGenderFilter({ gender: value }));
  };

  const handleLocationStatsTypeClick = (value) => {
    dispatch(setLocationStatsTypeFilter({ locationStatsType: value }));
  };

  const handleBotCheckClick = (value) => {
    let isBot;
    if (value === "true") isBot = true;
    else if (value === "false") isBot = false;
    else isBot = "";
    dispatch(setBotFilter({ isBot }));
  };

  const handleIosUserClick = (value) => {
    let isIosUser;
    if (value === "true") isIosUser = true;
    else if (value === "false") isIosUser = false;
    else isIosUser = null;
    dispatch(setIosUserFilter({ isIosUser }));
  };

  const handleReset = () => {
    dispatch(setResetFilter());
    dispatch(setDateFilter({ dateRange: defaultDateRange }));
    // showSuccessNotification('Filters are now set to their default values.');
  };
  const handleChangeUTM = (event) => {
    const { name, value } = event.target;
    dispatch(setUTMFilter({ [name]: value }));
  };

  return (
    <>
      <div className="d-flex gap-2 justify-content-between align-items-center py-16">
        {type !== 'chat' ?
          <div className="">
          </div> : null}

        <div className="d-flex gap-2 justify-content-between align-items-center">

          {type !== "google-analytics" ? <div className="bg-light-100 d-flex align-items-center gap-3 radius-8" style={{ border: '1px solid #d1d5db', }}>
            <CountrySelect />
            {selectedCountry ?
              <FilterState /> : null}
          </div> : null}

          {type !== 'chat' ? <>
            {/* On Channel Attribution's member list this picker is seeded from the node's
                window and writes `from`/`to` back to that page's URL (see Members.js) —
                getUsers DELETES the middleware's `createdAt` under source=attribution, so the
                write-back is what makes it filter anything at all. */}
            <BasicRangeShortcuts id="range1" />
            {type !== "google-analytics" ? <FormControl
              size="small"
              sx={{
                minWidth: 120,
                boxShadow: 'none',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  background: '#f3f4f6',
                  boxShadow: 'none',
                  '& fieldset': {
                    borderColor: '#d1d5db', // Default border color
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#9ca3af', // Hover border color (gray-400)
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2563eb', // Focused border (blue-600)
                    borderWidth: '1.5px',
                  },
                },
              }}
            >
              <Select
                value={selectedGender || ''}
                onChange={(e) => handleGenderClick(e.target.value)}
                IconComponent={KeyboardArrowDownOutlinedIcon} // custom arrow icon
                displayEmpty
                sx={{
                  fontSize: '14px',
                  height: '40px',
                  '& .MuiSelect-select': {
                    padding: '8px 12px',
                    fontSize: '14px',
                  },
                }}
              >
                <MenuItem value="" sx={{ fontSize: '14px' }}>
                  All Gender
                </MenuItem>
                <MenuItem value="male" sx={{ fontSize: '14px' }}>
                  Male
                </MenuItem>
                <MenuItem value="female" sx={{ fontSize: '14px' }}>
                  Female
                </MenuItem>
              </Select>
            </FormControl> : null}

            {/* Location Insights: All / Ghost / Active user-type filter.
                Sent to /stat/location as statsType, reusing the dashboard
                ghostUsers / completedSignup definitions (completed = Active, i.e. Approved + Hidden). */}
            {isLocationInsights ? <FormControl
              size="small"
              sx={{
                minWidth: 140,
                boxShadow: 'none',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  background: '#f3f4f6',
                  boxShadow: 'none',
                  '& fieldset': {
                    borderColor: '#d1d5db',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#9ca3af',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2563eb',
                    borderWidth: '1.5px',
                  },
                },
              }}
            >
              <Select
                value={selectedLocationStatsType || 'active_users'}
                onChange={(e) => handleLocationStatsTypeClick(e.target.value)}
                IconComponent={KeyboardArrowDownOutlinedIcon}
                displayEmpty
                sx={{
                  fontSize: '14px',
                  height: '40px',
                  '& .MuiSelect-select': {
                    padding: '8px 12px',
                    fontSize: '14px',
                  },
                }}
              >
                <MenuItem value="total_users" sx={{ fontSize: '14px' }}>
                  All Users
                </MenuItem>
                {/* Canonical member statuses (server statsMap). This used to send
                    `complete_signup` — the signup-form flag, which also counts inactive,
                    deactivated and suspended members — under the "Active" label. */}
                {LOCATION_STATUS_OPTIONS.map(({ value, status }) => (
                  <MenuItem key={value} value={value} sx={{ fontSize: '14px' }}>
                    {status === 'active' ? `${STATUS_LABEL.active} (Approved + Hidden)` : STATUS_LABEL[status]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> : null}


            {/* {isBotManagement ? (
              <>
                {isHideUserManagement && (<select
                  className="badge text-sm fw-medium ps-6 pe-20 py-12 radius-6 form-select mxheight40 bg-warning-900 text-white"
                  value="true"
                  disabled
                  style={{ minWidth: 120 }}
                >
                  <option value="true">Bots</option>
                </select>)}
              </>
            ) : isUserManagement ? (
              <>
                {isHideUserManagement && (<select
                  className="badge text-sm fw-medium ps-6 pe-20 py-12 radius-6 form-select mxheight40 bg-light-100 text-secondary-light"
                  value="false"
                  disabled
                  style={{ minWidth: 120 }}
                >
                  <option value="false">Users</option>
                </select>)}
              </>
            ) : (
              <>
             {isHideUserManagement && (
              <select
                className={`badge text-sm fw-medium ps-6 pe-20 py-12 radius-6 form-select mxheight40 ${selectedBotFilter
                  ? 'bg-info-100 text-black'
                  : 'bg-light-100 text-secondary-light'
                  }`}
                value={selectedBotFilter === true
                  ? "true"
                  : selectedBotFilter === false
                    ? "false"
                    : ""
                }
                onChange={(e) => handleBotCheckClick(e.target.value)}
              >
                <option value={null} disabled hidden>
                  All
                </option>
                <option value={""}>All</option>
                <option value={"false"}>Users</option>
                <option value={"true"}>Bots</option>
              </select>
             )}
             </>
            )} */}
            {isBotManagement ? (
              <>
                {isHideUserManagement && (
                  <FormControl size="small"
                    sx={{
                      minWidth: 120,
                      boxShadow: 'none',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        background: '#f3f4f6',
                        boxShadow: 'none',
                        '& fieldset': {
                          borderColor: '#d1d5db', // Default border color
                          borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9ca3af', // Hover border color (gray-400)
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2563eb', // Focused border (blue-600)
                          borderWidth: '1.5px',
                        },
                      },
                    }}>
                    <Select
                      value="true"
                      disabled
                      IconComponent={KeyboardArrowDownOutlinedIcon}
                      displayEmpty
                      sx={{
                        fontSize: '14px',
                        height: '40px',
                        '& .MuiSelect-select': {
                          padding: '8px 12px',
                          fontSize: '14px',
                        },
                      }}
                    >
                      <MenuItem value="true" sx={{ fontSize: '14px' }}>
                        Bots
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              </>
            ) : isUserManagement ? (
              <>
                {isHideUserManagement && (
                  <FormControl size="small"
                    sx={{
                      minWidth: 120,
                      boxShadow: 'none',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        background: '#f3f4f6',
                        boxShadow: 'none',
                        '& fieldset': {
                          borderColor: '#d1d5db', // Default border color
                          borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9ca3af', // Hover border color (gray-400)
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2563eb', // Focused border (blue-600)
                          borderWidth: '1.5px',
                        },
                      },
                    }}>
                    <Select
                      value="false"
                      disabled
                      IconComponent={KeyboardArrowDownOutlinedIcon}
                      displayEmpty
                      sx={{
                        fontSize: '14px',
                        height: '40px',
                        '& .MuiSelect-select': {
                          padding: '8px 12px',
                          fontSize: '14px',
                        },
                      }}
                    >
                      <MenuItem value="false" sx={{ fontSize: '14px' }}>
                        Users
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              </>
            ) : (
              <>
                {isHideUserManagement && (
                  <FormControl size="small"
                    sx={{
                      minWidth: 120,
                      boxShadow: 'none',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        background: '#f3f4f6',
                        boxShadow: 'none',
                        '& fieldset': {
                          borderColor: '#d1d5db', // Default border color
                          borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9ca3af', // Hover border color (gray-400)
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2563eb', // Focused border (blue-600)
                          borderWidth: '1.5px',
                        },
                      },
                    }}>
                    <Select
                      value={
                        selectedBotFilter === true
                          ? 'true'
                          : selectedBotFilter === false
                            ? 'false'
                            : ''
                      }
                      onChange={(e) => handleBotCheckClick(e.target.value)}
                      IconComponent={KeyboardArrowDownOutlinedIcon}
                      displayEmpty
                      sx={{
                        fontSize: '14px',
                        height: '40px',
                        '& .MuiSelect-select': {
                          padding: '8px 12px',
                          fontSize: '14px',
                        },
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: '14px' }}>
                        All
                      </MenuItem>
                      <MenuItem value="false" sx={{ fontSize: '14px' }}>
                        Users
                      </MenuItem>
                      <MenuItem value="true" sx={{ fontSize: '14px' }}>
                        Bots
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              </>
            )}

            {isUserManagement && (
              <FormControl
                size="small"
                sx={{
                  minWidth: 140,
                  boxShadow: 'none',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    background: '#f3f4f6',
                    boxShadow: 'none',
                    '& fieldset': {
                      borderColor: '#d1d5db',
                      borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#9ca3af',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2563eb',
                      borderWidth: '1.5px',
                    },
                  },
                }}
              >
                <Select
                  value={
                    selectedIosUserFilter === true
                      ? 'true'
                      : selectedIosUserFilter === false
                        ? 'false'
                        : ''
                  }
                  onChange={(e) => handleIosUserClick(e.target.value)}
                  IconComponent={KeyboardArrowDownOutlinedIcon}
                  displayEmpty
                  sx={{
                    fontSize: '14px',
                    height: '40px',
                    '& .MuiSelect-select': {
                      padding: '8px 12px',
                      fontSize: '14px',
                    },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '14px' }}>
                    All Platforms
                  </MenuItem>
                  <MenuItem value="true" sx={{ fontSize: '14px' }}>
                    iOS Users
                  </MenuItem>
                  <MenuItem value="false" sx={{ fontSize: '14px' }}>
                    Non-iOS Users
                  </MenuItem>
                </Select>
              </FormControl>
            )}

            {isUserManagement && <ProfileCompilationDropdown />}
            <Button
              className=""
              onClick={() => handleReset()}
              sx={{
                textTransform: 'capitalize',
                fontSize: '14px',
                fontWeight: '500',
                color: '#000',
                height: '40px',
                borderRadius: '8px',
                border: '1px solid #d1d5db', // light gray border
                backgroundColor: '#fff',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#BE1F1F', // blue hover
                  color: '#fff', // white text on hover
                  borderColor: '#ff4A4A', // blue border
                },
              }}
            >
              Reset
            </Button></> : null}
        </div>
      </div>
      {/* {isHideSelectCam && (<div className='d-flex gap-2 py-16 justify-content-between align-items-center mt-2'>
        <div className='dseltnew w-100'>
          <select className='form-select' name='utm_campaign' value={utmPayload.utm_campaign || ""} onChange={handleChangeUTM}>
            <option value="">Select Campaign</option>
            {utmOptions.utm_campaign.map((item, i) => (
              <option key={i} value={item}>{item}</option>
            ))}
          </select>
        </div>

        <div className='dseltnew w-100'>
          <select className='form-select' name='utm_content' value={utmPayload.utm_content || ""} onChange={handleChangeUTM}>
            <option value="">Select Content</option>
            {utmOptions.utm_content.map((item, i) => (
              <option key={i} value={item}>{item}</option>
            ))}
          </select>
        </div>

        <div className='dseltnew w-100'>
          <select className='form-select' name='utm_medium' value={utmPayload.utm_medium || ""} onChange={handleChangeUTM}>
            <option value="">Select Medium</option>
            {utmOptions.utm_medium.map((item, i) => (
              <option key={i} value={item}>{item}</option>
            ))}
          </select>
        </div>

        <div className='dseltnew w-100'>
          <select className='form-select' name='utm_source' value={utmPayload.utm_source || ""} onChange={handleChangeUTM}>
            <option value="">Select Source</option>
            {utmOptions.utm_source.map((item, i) => (
              <option key={i} value={item}>{item}</option>
            ))}
          </select>
        </div>

        <div className='dseltnew w-100'>
          <select className='form-select' name='utm_term' value={utmPayload.utm_term || ""} onChange={handleChangeUTM}>
            <option value="">Select Term</option>
            {utmOptions.utm_term.map((item, i) => (
              <option key={i} value={item}>{item}</option>
            ))}
          </select>
        </div>
      </div>
      )} */}
    </>
  );
};

export default SearchFilter; 